import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { CONFIG } from "../config";

const TopFailuresGraph = ({ selectedDate }) => {
  const containerRef = useRef();
  const svgRef = useRef();
  const rowHeight = 50;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Resize observer for responsiveness
  // useEffect(() => {
  //   const observer = new ResizeObserver((entries) => {
  //     const entry = entries[0];
  //     const { width } = entry.contentRect;
  //     setDimensions({ width, height: 120 }); // fixed height
  //   });

  //   if (containerRef.current) observer.observe(containerRef.current);
  //   return () => observer.disconnect();
  // }, []);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width } = entry.contentRect;
      setDimensions(prev => {
        // Only update if width changes
        if (prev.width !== width) {
          return { ...prev, width };
        }
        return prev;
      });
    });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);


  // Fetch data from API
  useEffect(() => {
    if (!selectedDate) return; // wait until selectedDate is available

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${CONFIG.API_BASE_URL}/${CONFIG.TOP_FAILURES_PREDICTION_GRAPH_URL}/${localStorage.getItem(
            "tensor-user-id"
          )}?date_str=${selectedDate}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("tensor-auth-token"),
          },
        });

        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          console.dir("result.data");
          console.dir(result.data);
          console.dir("result.data");
          setData(result.data);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Failed to fetch failures prevented data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate]);


useEffect(() => {
  if (!data.length || !dimensions.width) return;
  
  const drawChart = () => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const fullWidth = svgRef.current?.parentElement?.offsetWidth || 500;
    const margin = { top: 30, right: 30, bottom: 10, left: 300 };
    const height = margin.top + data.length * rowHeight + margin.bottom;
    const barWidth = fullWidth - margin.left - margin.right;
    const dynamicHeight = margin.top + data.length * rowHeight + margin.bottom;

    svg
      .attr("width", fullWidth)
      .attr("height", dynamicHeight)
      .attr("viewBox", `0 0 ${fullWidth} ${dynamicHeight}`)
      .attr("preserveAspectRatio", "xMinYMin meet");

    const maxValue = d3.max(data, d => d.last6 + d.next4);
    const xScale = d3.scaleLinear().domain([0, maxValue]).range([0, barWidth]);
    const yScale = d3.scaleBand()
      .domain(data.map(d => d.issue))
      .range([margin.top, height - margin.bottom])
      .padding(0.5);

    const g = svg.append("g").attr("transform", `translate(${margin.left}, 0)`);

    // svg.selectAll(".issue-label")
    //   .data(data)
    //   .enter()
    //   .append("text")
    //   .attr("x", 5)
    //   .attr("y", d => yScale(d.issue) + yScale.bandwidth() / 2 + 4)
    //   .attr("font-size", "16px")
    //   .attr("font-weight", "500")
    //   .attr("fill", "#1C2024")
    //   .text((d, i) => `${i + 1}. ${d.issue}`);

    svg.selectAll(".issue-label")
      .data(data)
      .enter()
      .append("text")
      .attr("x", 5)
      .attr("y", d => yScale(d.issue) + yScale.bandwidth() / 2)
      .style("font-family", "Noto Sans")
      .style("font-size", "16px")
      .style("font-style", "normal")
      .style("font-weight", 400)
      .style("fill", "#1C2024")
      .attr("alignment-baseline", "middle") // helps center vertically
      .text((d, i) => `${i + 1}. ${d.issue}`);



    g.selectAll(".bar-last6")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", 0)
      .attr("y", d => yScale(d.issue))
      .attr("width", d => xScale(d.last6))
      .attr("height", yScale.bandwidth())
      .attr("fill", "#0037FF");

    g.selectAll(".bar-next4")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", d => xScale(d.last6))
      .attr("y", d => yScale(d.issue))
      .attr("width", d => xScale(d.next4))
      .attr("height", yScale.bandwidth())
      .attr("fill", "#7CA6FF");

    g.selectAll(".text-last6")
      .data(data)
      .enter()
      .append("text")
      .attr("x", -35)
      .attr("y", d => yScale(d.issue) + yScale.bandwidth() / 2 + 4)
      .attr("fill", "#0037FF")
      .style("font-family", "Noto Sans")
      .style("font-size", "16px")
      .style("font-style", "normal")
      .attr("font-weight", "700")
      .text(d => d.last6);

    g.selectAll(".text-next4")
      .data(data)
      .enter()
      .append("text")
      .attr("x", d => xScale(d.last6 + d.next4) + 5)
      .attr("y", d => yScale(d.issue) + yScale.bandwidth() / 2 + 4)
      .attr("fill", "#7CA6FF")
      .style("font-family", "Noto Sans")
      .style("font-size", "16px")
      .style("font-style", "normal")
      .attr("font-weight", "700")
      .text(d => d.next4);

    const legend = svg.append("g").attr("transform", `translate(10, ${margin.top})`);
    legend.append("circle").attr("cx", 0).attr("cy", -20).attr("r", 6).attr("fill", "#0037FF");
    legend.append("text").attr("x", 12).attr("y", -16).text("Last 6 weeks").style("font-size", "12px");
    legend.append("circle").attr("cx", 100).attr("cy", -20).attr("r", 6).attr("fill", "#7CA6FF");
    legend.append("text").attr("x", 110).attr("y", -16).text("Next 4 weeks").style("font-size", "12px");
  };

  drawChart(); // Initial draw

  window.addEventListener("resize", drawChart);
  return () => window.removeEventListener("resize", drawChart);
}, [data, dimensions]);


  return (
    <div ref={containerRef}>
      <h2 className="text-xl font-semibold mb-4 text-2xl font-bold truck_page_head_text">
        Top Failures (Actual vs. Predicted)
      </h2>
      {loading && (
        <div className="flex justify-center items-center min-h-[180px]">
          <svg className="w-6 h-6 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
            <path className="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z">
            </path>
          </svg>
        </div>
      )}

      {!loading && (
        <div className="pt-4 pr-4 pl-4 pb-0 bg-white">
          <div className="w-full overflow-x-auto">
            <svg ref={svgRef}></svg>
          </div>
        </div>        
      )}

    </div>
  );
};

export default TopFailuresGraph;
